/* Censure — compact layout. The rail holds three sections, each with a count:
   Banned words (opens first), Discouraged themes (active/total) and Ban themes
   (read-only, marked with a lock). Every change saves on its own, like Content
   and Monetisation — there is no Save button. Theme toggles, adds and deletes
   save straight away; typing is debounced. A theme is only sent once it has a
   name and a detection prompt. Ban themes are set by the platform and are only
   listed here: no toggles, no add, no delete. */

import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { Account, CensureTopicTheme } from '../../../../api/client';
import { useAutosave } from '../../../../hooks/useAutosave';
import { Btn, Card, ErrorLine, Icon, Loading, PageFrame, SaveStatus, SectionRail, Stack, Toggle, Warn, type RailRow } from '../kit';
import { font as f, mergeStatus } from '../style';
import { C } from '../tokens';
import { SECS, newTheme, parseWords, themeOf, themeProblem, themeSync, type Sec, type Theme } from './model';

type Load = 'loading' | 'ready' | 'failed';
const PAGE = 120;

export function CensurePage({ account }: { account: Account }) {
  const { t } = useTranslation('assistantCompact');
  const id = account.account_id;

  const [params, setParams] = useSearchParams();
  const sec: Sec = SECS.find((s) => s === params.get('s')) ?? 'words';
  const select = (s: string) => { const n = new URLSearchParams(params); n.set('s', s); setParams(n, { replace: true }); };

  // ── Load ─────────────────────────────────────────────────────────
  const [themes, setThemes] = useState<Theme[]>([]);
  const [themesLoad, setThemesLoad] = useState<Load>('loading');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [words, setWords] = useState<string[]>([]);
  const [wordsLoad, setWordsLoad] = useState<Load>('loading');
  const [bans, setBans] = useState<CensureTopicTheme[]>([]);
  const [bansLoad, setBansLoad] = useState<Load>('loading');
  const sync = useRef<ReturnType<typeof themeSync> | null>(null);

  useEffect(() => {
    let off = false;
    api.getCensureNotPreferableThemes(id).then(
      (rows) => {
        if (off) return;
        const list = rows.map(themeOf);
        sync.current = themeSync(id, list);
        setThemes(list);
        setOpen(list[0] ? { [list[0].key]: true } : {});
        setThemesLoad('ready');
      },
      () => { if (!off) setThemesLoad('failed'); },
    );
    api.getBannedWords(id).then(
      (r) => { if (!off) { setWords(r.words ?? []); setWordsLoad('ready'); } },
      () => { if (!off) setWordsLoad('failed'); },
    );
    api.getCensureBanThemes(id).then(
      (rows) => { if (!off) { setBans(rows.filter((r) => r.is_active)); setBansLoad('ready'); } },
      () => { if (!off) setBansLoad('failed'); },
    );
    return () => { off = true; };
  }, [id]);

  // ── Autosave ─────────────────────────────────────────────────────
  // Nothing is saved for a part that failed to load: saving the empty
  // fallback would wipe what's really stored.
  const themeAuto = useAutosave<Theme[]>({
    value: themes,
    enabled: themesLoad === 'ready',
    delay: 600,
    save: async (d) => {
      if (!sync.current) return;
      const created = await sync.current(d);
      if (Object.keys(created).length) setThemes((l) => l.map((x) => { const nid = created[x.key]; return nid === undefined ? x : { ...x, id: nid }; }));
    },
  });
  const wordAuto = useAutosave<string[]>({
    value: words,
    enabled: wordsLoad === 'ready',
    delay: 300,
    save: async (w) => { await api.updateBannedWords(id, w); },
  });
  const save = mergeStatus(themeAuto, wordAuto);

  const setThemesNow = (fn: (l: Theme[]) => Theme[]) => { setThemes(fn); window.setTimeout(themeAuto.flush, 0); };

  // ── Rail ─────────────────────────────────────────────────────────
  const fmt = (n: number) => n.toLocaleString();
  const act = themes.filter((x) => x.on).length;
  const rail: RailRow[] = [
    { id: 'words', label: t('ce.s.words'), meta: wordsLoad === 'ready' ? fmt(words.length) : undefined },
    { id: 'themes', label: t('ce.s.themes'), meta: themesLoad === 'ready' ? `${act}/${themes.length}` : undefined, metaTitle: t('ce.activeTip', { n: act, total: themes.length }) },
    { id: 'bans', label: t('ce.s.bans'), meta: bansLoad === 'ready' ? String(bans.length) : undefined, icon: 'lock', iconTitle: t('ce.readOnly') },
  ];

  const state = sec === 'words' ? wordsLoad : sec === 'themes' ? themesLoad : bansLoad;

  return (
    <PageFrame
      rail={<SectionRail heading={t('ce.rail')} items={rail} active={sec} onSelect={select} width={220} />}
      headline={t(`ce.s.${sec}`)}
      desc={t(`ce.d.${sec}`)}
      right={sec === 'bans' ? undefined : <SaveStatus status={save.status} error={save.error} onRetry={save.retry} idleLabel />}
    >
      {state === 'loading' && <Loading />}
      {state === 'failed' && <ErrorLine>{t('loadFailed')}</ErrorLine>}
      {state === 'ready' && sec === 'words' && <Words words={words} setWords={setWords} />}
      {state === 'ready' && sec === 'themes' && (
        <Themes
          themes={themes}
          open={open}
          onOpen={(k) => setOpen((o) => ({ ...o, [k]: !o[k] }))}
          onAdd={() => { const n = newTheme(); setThemes((l) => [...l, n]); setOpen((o) => ({ ...o, [n.key]: true })); }}
          onEdit={(k, p) => setThemes((l) => l.map((x) => (x.key === k ? { ...x, ...p } : x)))}
          onToggle={(k, v) => setThemesNow((l) => l.map((x) => (x.key === k ? { ...x, on: v } : x)))}
          onDelete={(k) => setThemesNow((l) => l.filter((x) => x.key !== k))}
        />
      )}
      {state === 'ready' && sec === 'bans' && <Bans bans={bans} />}
    </PageFrame>
  );
}

/* ── Shared bits ─────────────────────────────────────────────────── */
const field: CSSProperties = { height: 30, boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 7, padding: '0 10px', color: C.text, font: f(500, 12.5), outline: 'none', minWidth: 0 };
const countStyle: CSSProperties = { font: f(500, 12), color: C.sub, whiteSpace: 'nowrap' };
const capStyle: CSSProperties = { font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted };

function Search({ value, onChange, placeholder, style }: { value: string; onChange: (v: string) => void; placeholder: string; style?: CSSProperties }) {
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', ...style }}>
      <Icon name="search" size={12} color={C.muted} style={{ position: 'absolute', left: 9, pointerEvents: 'none' }} />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ ...field, width: '100%', paddingLeft: 28 }} />
    </div>
  );
}

/* ── Banned words ────────────────────────────────────────────────── */
function Words({ words, setWords }: { words: string[]; setWords: (fn: (w: string[]) => string[]) => void }) {
  const { t } = useTranslation('assistantCompact');
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState('');
  const [shown, setShown] = useState(PAGE);
  // Words added on this visit go first and stay tinted.
  const [added, setAdded] = useState<string[]>([]);

  const add = () => {
    const { fresh, dup } = parseWords(input, words);
    if (fresh.length) {
      setWords((w) => [...fresh, ...w.filter((x) => !fresh.includes(x))]);
      setAdded((a) => [...fresh, ...a]);
    }
    setInput('');
    setQ('');
    setMsg(dup.length === 1 ? t('ce.w.dupOne', { w: dup[0] }) : dup.length ? t('ce.w.dupMany', { n: dup.length }) : '');
  };
  const remove = (w: string) => { setWords((l) => l.filter((x) => x !== w)); setAdded((a) => a.filter((x) => x !== w)); };

  const needle = q.trim().toLowerCase();
  const list = needle ? words.filter((w) => w.includes(needle)) : words;
  const fmt = (n: number) => n.toLocaleString();
  const count = needle
    ? t('ce.w.matches', { count: list.length, n: fmt(list.length) })
    : t('ce.w.count', { count: words.length, n: fmt(words.length) }) + (added.length ? ` · ${t('ce.w.added', { n: added.length })}` : '');

  return (
    <Stack gap={12}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input
          value={input}
          onChange={(e) => { setInput(e.target.value); setMsg(''); }}
          onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={t('ce.w.addPh')}
          style={{ ...field, flex: '1 1 260px' }}
        />
        <Btn kind="primary" icon="plus" height={30} disabled={!input.trim()} onClick={add}>{t('ce.w.add')}</Btn>
        <Search value={q} onChange={(v) => { setQ(v); setShown(PAGE); }} placeholder={t('ce.w.searchPh')} style={{ flex: '0 1 220px', minWidth: 160 }} />
      </div>
      {msg && <Warn>{msg}</Warn>}
      <span style={countStyle}>{count}</span>
      <Card clip={false} style={{ padding: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {list.slice(0, shown).map((w) => {
          const isNew = added.includes(w);
          return (
            <span key={w} style={{ height: 26, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 4, padding: '0 4px 0 9px', borderRadius: 6, border: `1px solid ${isNew ? 'color-mix(in srgb, var(--om-accent) 55%, transparent)' : C.border}`, background: isNew ? C.tint16 : C.input, color: C.text, font: f(500, 12), whiteSpace: 'nowrap' }}>
              {w}
              <button type="button" title={t('ce.w.remove')} onClick={() => remove(w)} className="cpt-hover-text" style={{ width: 18, height: 18, border: 0, borderRadius: 4, background: 'transparent', color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                <Icon name="x" size={11} />
              </button>
            </span>
          );
        })}
        {!list.length && <div style={{ flex: 1, padding: 16, textAlign: 'center', font: f(400, 12), color: C.muted }}>{words.length ? t('ce.w.noMatch') : t('ce.w.none')}</div>}
      </Card>
      {list.length > shown && (
        <Btn style={{ alignSelf: 'center' }} onClick={() => setShown((s) => s + PAGE)}>{t('ce.w.more', { n: fmt(list.length - shown) })}</Btn>
      )}
    </Stack>
  );
}

/* ── Discouraged themes ──────────────────────────────────────────── */
function Themes({ themes, open, onOpen, onAdd, onEdit, onToggle, onDelete }: {
  themes: Theme[];
  open: Record<string, boolean>;
  onOpen: (k: string) => void;
  onAdd: () => void;
  onEdit: (k: string, p: Partial<Theme>) => void;
  onToggle: (k: string, v: boolean) => void;
  onDelete: (k: string) => void;
}) {
  const { t } = useTranslation('assistantCompact');
  const act = themes.filter((x) => x.on).length;
  return (
    <Stack gap={12}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={countStyle}>{t('ce.t.count', { count: themes.length, active: act })}</span>
        <span style={{ flex: 1 }} />
        <Btn kind="primary" icon="plus" onClick={onAdd}>{t('ce.t.add')}</Btn>
      </div>
      <Card>
        {themes.map((x) => {
          const o = !!open[x.key];
          const problem = themeProblem(x, themes);
          return (
            <div key={x.key} style={{ display: 'flex', flexDirection: 'column' }}>
              <div role="button" tabIndex={0} onClick={() => onOpen(x.key)} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(x.key); } }} className="cpt-hover-row" style={{ height: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '0 10px 0 12px', cursor: 'pointer' }}>
                <Icon name="chevron-right" size={13} color={C.muted} style={{ transform: o ? 'rotate(90deg)' : 'none', transition: 'transform .15s', flex: 'none' }} />
                <span style={{ font: f(600, 13), color: x.on ? C.text : C.sub, whiteSpace: 'nowrap', maxWidth: '45%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.name.trim() || t('ce.t.untitled')}</span>
                <span style={{ flex: 1, minWidth: 0, font: f(400, 12), color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o ? '' : (x.prompt.split('\n').find((l) => l.trim()) ?? t('ce.t.noPrompt'))}</span>
                {problem && !o && <Icon name="alert-circle" size={13} color={C.purplePale} style={{ flex: 'none' }} />}
                <Toggle on={x.on} onChange={(v) => onToggle(x.key, v)} title={x.on ? t('ce.t.onTip') : t('ce.t.offTip')} />
                <button type="button" title={t('ce.t.del')} onClick={(e) => { e.stopPropagation(); onDelete(x.key); }} className="cpt-hover-text" style={{ width: 26, height: 26, flex: 'none', borderRadius: 6, border: 0, background: 'transparent', color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="trash-2" size={13} />
                </button>
              </div>
              {o && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 14px 14px 35px' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={capStyle}>{t('ce.t.name')}</span>
                    <input value={x.name} onChange={(e) => onEdit(x.key, { name: e.target.value })} placeholder={t('ce.t.namePh')} style={{ ...field, maxWidth: 320 }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={capStyle}>{t('ce.t.prompt')}</span>
                    <textarea value={x.prompt} onChange={(e) => onEdit(x.key, { prompt: e.target.value })} placeholder={t('ce.t.promptPh')} style={{ minHeight: 72, resize: 'vertical', boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 7, padding: '8px 10px', color: C.text, font: f(400, 12.5, 1.5), outline: 'none' }} />
                  </label>
                  {problem && <Warn>{problem === 'duplicate' ? t('ce.t.dup') : x.id === null ? t('ce.t.incompleteNew') : t('ce.t.incomplete')}</Warn>}
                </div>
              )}
            </div>
          );
        })}
        {!themes.length && <div style={{ padding: 24, textAlign: 'center', font: f(400, 12), color: C.muted }}>{t('ce.t.none')}</div>}
      </Card>
    </Stack>
  );
}

/* ── Ban themes (read-only) ──────────────────────────────────────── */
function Bans({ bans }: { bans: CensureTopicTheme[] }) {
  const { t } = useTranslation('assistantCompact');
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const list = needle ? bans.filter((b) => b.topic_heading.toLowerCase().includes(needle)) : bans;
  return (
    <Stack gap={12}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, font: f(500, 12), color: C.sub }}>
        <Icon name="lock" size={12} color={C.purpleLt} />
        {t('ce.b.note')}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={countStyle}>{needle ? t('ce.b.countOf', { n: list.length, total: bans.length }) : t('ce.b.count', { count: bans.length })}</span>
        <span style={{ flex: 1 }} />
        <Search value={q} onChange={setQ} placeholder={t('ce.b.searchPh')} style={{ width: 220 }} />
      </div>
      <Card>
        {list.map((b) => (
          <div key={b.id} style={{ padding: '10px 14px', font: f(600, 13), color: C.text }}>{b.topic_heading}</div>
        ))}
        {!list.length && <div style={{ padding: 24, textAlign: 'center', font: f(400, 12), color: C.muted }}>{bans.length ? t('ce.b.noMatch') : t('ce.b.none')}</div>}
      </Card>
    </Stack>
  );
}
